import { useId, useMemo, useState } from "react";
import type {
  BuyerResearchRefinementCostDto,
  BuyerResearchRefinementDto,
  BuyerResearchRefinementStateDto,
  BuyerResearchRefinementStatus,
} from "@/api/buyerResearch";
import { Button, Label, StatusBadge, Textarea, Timestamp } from "@/design-system/components/ui";
import { useUserTimeZone } from "@/utils/datetime";
import { AskPhoenixHistory } from "./AskPhoenixHistory";
import { researchCopy } from "./researchCopy";
import {
  formatCost,
  refinementBlockerSentence,
  refinementRefusalMessage,
  refinementStatusTone,
} from "./researchFormat";
import { useClearRefinement, useConfirmRefinement, useRefineRun } from "./useBuyerResearch";

/**
 * The quote a submitted refinement was priced at.
 *
 * <p>🔴 <b>KEYED ON THE REFINEMENT IT PRICED.</b> The cost arrives on the
 * classify response and on nothing else — the run's refinement state carries no
 * cost — so it lives in the browser that submitted, for as long as the run is
 * open. A bare cost object would caption whatever refinement happened to be
 * pending when it was read, and a stale one would quote yesterday's price for
 * today's question. The id is what makes that impossible.</p>
 *
 * <p>🔴 <b>HOISTED BECAUSE WS6 PUT THE TWO HALVES ON OPPOSITE SIDES OF THE
 * LIST.</b> The INPUT takes the quote at submit; the STATE panel spends it on
 * the Confirm button. They were one component and are two, so the value has to
 * outlive both — held by the run, which is keyed on the run id and therefore
 * cannot carry a quote from one run to another.</p>
 */
export interface AskPhoenixQuoteState {
  value: { refinementId: string; cost: BuyerResearchRefinementCostDto } | null;
  set: (next: { refinementId: string; cost: BuyerResearchRefinementCostDto } | null) => void;
}

export function useAskPhoenixQuote(): AskPhoenixQuoteState {
  const [value, set] = useState<AskPhoenixQuoteState["value"]>(null);
  return useMemo(() => ({ value, set }), [value]);
}

/**
 * Ask Phoenix — a specialist narrows a finished run in their own words.
 *
 * <p>Three things happen here and only three: somebody types an instruction,
 * Phoenix says what it made of it and what acting on it would cost, and
 * somebody approves or discards. Every figure on screen is the server's;
 * nothing below computes a price, a page size or a budget.</p>
 *
 * <p>🔴 <b>RF13 — THE COST IS BESIDE THE BUTTON, BEFORE THE PRESS.</b> Not a
 * modal, not a toast afterwards. The whole of decision 1 is that the count is
 * free, the number is on screen, and nothing is bought until somebody clicks —
 * which is worth nothing if the number arrives after the click. Submit is
 * <c>secondary</c>, the same weight the run's other spending controls carry.</p>
 *
 * <p>🔴 <b>RF6 — TWO BUDGETS, TWO SENTENCES.</b> Records come off Clef's shared
 * daily allowance and reset at midnight UTC; model spend comes off this run's
 * own cap, which is configuration. Every sentence that names a cost names which
 * budget it comes out of, and no sentence covers both.</p>
 *
 * <p>🔴 <b>DECISION 5 — A REFINEMENT IS STICKY ON THE RUN.</b> Reopen it
 * tomorrow, or as a colleague who typed none of it, and it is still applied
 * with the ORIGINAL WORDING shown. That is why the applied block renders for a
 * reader who cannot edit: what you are looking at is a filtered list, and a
 * reader who cannot see the filter cannot read the list.</p>
 *
 * <p>Actions are HIDDEN rather than disabled for a reader who cannot edit — the
 * module convention.</p>
 */
export function AskPhoenixPanel({
  buyerLeadId,
  runId,
  state,
  canEdit,
  quote,
}: {
  buyerLeadId: string;
  runId: string;
  /** Ask Phoenix state, riding the same read as the candidates it is a lens over. */
  state: BuyerResearchRefinementStateDto;
  canEdit: boolean;
  /** The quote the INPUT took at submit, which this panel spends. */
  quote: AskPhoenixQuoteState;
}) {
  const copy = researchCopy.askPhoenix;
  const tz = useUserTimeZone();

  const confirm = useConfirmRefinement(buyerLeadId);
  const clear = useClearRefinement(buyerLeadId);

  const [error, setError] = useState<string | null>(null);

  const applied = state.active != null && state.active.status === "applied" ? state.active : null;
  const inFlight = pickInFlight(state);
  const answered = pickUnacted(state, inFlight);
  const quoteFor = inFlight != null && quote.value?.refinementId === inFlight.id ? quote.value.cost : null;

  // Scoped to the row it was pressed on. An applied refinement and one still
  // waiting for approval can be on screen together, and a bare `isPending`
  // would spin both buttons off one press — the idiom the dismissal list
  // already uses for the same reason.
  const clearingId = clear.isPending ? (clear.variables?.refinementId ?? null) : null;

  const clearRefinement = (refinementId: string) => {
    setError(null);
    clear.mutate(
      { runId, refinementId },
      { onError: (e: unknown) => setError(refinementRefusalMessage(e)) },
    );
  };

  // No `border-t` on this section, and none on any sibling. The selected run
  // has SIX siblings — brief, query transcript, sources, Ask Phoenix, results,
  // pull — and THREE drew a rule above themselves: the transcript, Ask Phoenix
  // and this one. So the rule marked no boundary a reader could name; it marked
  // which components happened to have been written with one. 🔴 And the three
  // do not even share a shape: two of them render their own heading and the
  // pull control renders none, so the rule was not tracking "a titled section
  // starts here" either. §17.5 calls that a structural signal corresponding to
  // no structure; §17.2's heading ladder carries the division and the parent's
  // `gap-space-4` carries the spacing.
  //
  // ⚠️ The count is SIX, not the five the §17.4 note in `ResearchTab` cites:
  // that note is counting sections that render a HEADING, which the pull
  // control does not. Re-derived rather than copied.
  // 🔴 NOTHING TO SAY, SO NOTHING IS SAID. Before WS6 this section always
  // rendered, because the input lived inside it and the input is what a fresh
  // run has. With the input at the foot of the list a run nobody has narrowed
  // has no lens, no answer and no history — and a lone heading above the
  // candidates is a row of chrome telling a reader about a feature they have
  // not used, on the one surface this workstream is about shortening.
  //
  // 🔴 THE HISTORY TERM IS LOAD-BEARING AND WAS MISSING FROM MY FIRST PASS.
  // A run whose refinement was CLEARED has no applied lens, nothing in flight
  // and nothing unacted — and it still has a record of what was asked and
  // undone, which RF13 makes its own section precisely so it survives the lens
  // it describes. Without this term the panel returned null and took that
  // record with it. `mounts the run's refinement history` is the test that
  // caught it; the suite did its job and the guard did not.
  if (
    applied == null &&
    inFlight == null &&
    answered == null &&
    error == null &&
    state.history.length === 0
  ) {
    return null;
  }

  return (
    <section
      className="grid gap-space-2"
      data-testid="ask-phoenix"
      data-can-refine={state.canRefine}
    >
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">{copy.heading}</h3>

      {applied && (
        <div className="grid gap-space-1" data-testid="ask-phoenix-applied">
          <AskedLine refinement={applied} tz={tz} />
          {applied.rationale && (
            <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-rationale">
              {copy.understood} {applied.rationale}
            </p>
          )}
          {/* 🔴 THE WHOLE VISIBLE CHAIN, AND ONLY WHEN THERE IS ONE. The server
              sends `summary` null for a lone refinement, so this renders exactly
              when something was inherited — the gate is the SERVER's answer and
              is never re-derived from the history rows, which would put this
              line on a refinement that composed nothing. */}
          {applied.summary && (
            <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-chain">
              {copy.chain} {applied.summary}
            </p>
          )}
          <p className="m-0 text-caption text-gray-mid tabular-nums" data-testid="ask-phoenix-applied-counts">
            {copy.outcomeCounts(applied.visibleCount, applied.hiddenCount, applied.promotedCount)}
          </p>
          {canEdit && (
            <div className="grid gap-space-1">
              {/* 🔴 BEFORE THE PRESS. Clearing un-buys nothing and moves what
                  Pull more would buy next — a control that said either of those
                  only afterwards has already let somebody expect a refund. And
                  over a CHAIN the same button takes every step off at once,
                  which the unqualified sentence does not say. */}
              <p className="m-0 text-caption text-gray-mid break-words">
                {applied.summary ? copy.clearChainConsequence : copy.clearConsequence}
              </p>
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={clearingId === applied.id}
                  onClick={() => clearRefinement(applied.id)}
                  data-testid="ask-phoenix-clear"
                >
                  {clearingId === applied.id ? copy.clearPending : copy.clear}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {inFlight && (
        <div className="grid gap-space-1" data-testid="ask-phoenix-pending" data-status={inFlight.status}>
          <AskedLine refinement={inFlight} tz={tz} />
          {inFlight.rationale && (
            <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-pending-rationale">
              {copy.understood} {inFlight.rationale}
            </p>
          )}
          {/* 🔴 ONLY ONCE PHOENIX HAS ANSWERED. `kind` is a non-nullable string
              on a row that exists from the moment the instruction is accepted,
              so a refinement still being read carries whatever the server
              seeded it with — and rendering that would put a confident sentence
              about what this refinement DOES beside a pill saying Phoenix has
              not finished reading it. */}
          {inFlight.status !== "classifying" && (
            <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-kind">
              {copy.kind[inFlight.kind] ?? inFlight.kind}
            </p>
          )}

          {/* 🔴 A CONFIRMED BUY THAT FAILED COMES BACK HERE (R9). The worker puts
              the refinement back to `awaiting confirmation` with the reason on
              its `error` — the request that queued it has long since answered,
              so there is no request error to show. Without this line the
              specialist would see the same Confirm they pressed, with nothing to
              say it was pressed and did not work. */}
          {inFlight.status === "awaiting confirmation" && inFlight.error && (
            <p
              className="m-0 text-caption text-status-error break-words"
              data-testid="ask-phoenix-pending-error"
            >
              {inFlight.error}
            </p>
          )}

          {inFlight.status === "awaiting confirmation" && (
            <div className="grid gap-space-1">
              {/* 🔴 THE COST SENTENCE, BESIDE THE BUTTON. Two budgets, named
                  apart; `isFree` is the server's answer and never re-derived. */}
              <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-cost">
                {costSentences(quoteFor, inFlight).join(" ")}
              </p>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-space-2">
                  {/* 🔴 NO CONFIRM WITHOUT A QUOTE. Decision 1 is that nothing
                      is bought until somebody has been told what it costs, and
                      this browser holds the figure only for a refinement it
                      SUBMITTED itself — the quote arrives on the classify
                      response and on nothing else, so a reload or a colleague
                      has none. That is a real limitation and it is recoverable:
                      Discard is deliberately NOT gated on the quote, and asking
                      again costs one free count call. Confirming an unquoted
                      spend would cost records nobody agreed to. */}
                  {quoteFor != null && (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={confirm.isPending}
                      onClick={() => {
                        setError(null);
                        confirm.mutate(
                          { runId, refinementId: inFlight.id },
                          { onError: (e: unknown) => setError(refinementRefusalMessage(e)) },
                        );
                      }}
                      data-testid="ask-phoenix-confirm"
                    >
                      {confirm.isPending ? copy.confirmPending : copy.confirm}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="text"
                    loading={clearingId === inFlight.id}
                    onClick={() => clearRefinement(inFlight.id)}
                    data-testid="ask-phoenix-discard"
                  >
                    {clearingId === inFlight.id ? copy.discardPending : copy.discard}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {answered && (
        <div className="grid gap-space-1" data-testid="ask-phoenix-answered" data-status={answered.status}>
          <AskedLine refinement={answered} tz={tz} />
          {/* A refusal is an ANSWER and says why in Phoenix's own words; a
              failure is a fault and says so in red. They carry their reasons on
              two different fields, so each renders on the one it owns rather
              than on a shared line the status would have to disambiguate. */}
          {answered.refusalReason && (
            <p className="m-0 text-caption text-gray-mid break-words" data-testid="ask-phoenix-refusal">
              {answered.refusalReason}
            </p>
          )}
          {answered.error && (
            <p className="m-0 text-caption text-status-error break-words" data-testid="ask-phoenix-failed">
              {answered.error}
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="m-0 text-caption text-status-error" role="alert" data-testid="ask-phoenix-error">
          {error}
        </p>
      )}

      {/* RF13 — a SIBLING of the run's transcript, never an extension of it. */}
      <AskPhoenixHistory refinements={state.history} />
    </section>
  );
}


/**
 * Ask Phoenix's INPUT — at the foot of the list it acts on.
 *
 * <p>🔴 <b>THE SPLIT IS A RECORDED OWNER RULING, and it is scoped precisely.</b>
 * The applied lens stays ABOVE the list, because what a reader is looking at is
 * a filtered list and a reader who cannot see the filter cannot read the list.
 * The thing that SETS the filter is a different question: it acts on what is on
 * screen, so it belongs where what is on screen ends. The ruling as recorded:
 * <i>the state stays above the list; the INPUT may sit adjacent to the list or
 * stick to it.</i></p>
 *
 * <p>It also carries the BLOCKED sentence, which is not a separate decision:
 * where `canRefine` is false there is no input, and the sentence saying why is
 * what stands in its place. Putting the explanation above the list and the
 * control below it would split one answer across two screens of scrolling.</p>
 *
 * <p>🔴 <b>THE SUBMIT ERROR IS THIS COMPONENT'S OWN.</b> It used to share one
 * `error` with Confirm and Discard, which was fine while all three were in one
 * block and is wrong now: an error from a press at the foot of the list would
 * render above it, out of sight. Each half owns the errors its own buttons
 * raise.</p>
 */
export function AskPhoenixInput({
  buyerLeadId,
  runId,
  state,
  canEdit,
  quote,
}: {
  buyerLeadId: string;
  runId: string;
  state: BuyerResearchRefinementStateDto;
  canEdit: boolean;
  /** Where the quote this submit takes is put, for the state panel to spend. */
  quote: AskPhoenixQuoteState;
}) {
  const copy = researchCopy.askPhoenix;
  const fieldId = useId();
  const refine = useRefineRun(buyerLeadId);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const inFlight = pickInFlight(state);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setError(null);
    refine.mutate(
      { runId, text: trimmed },
      {
        onSuccess: (result) => {
          setText("");
          // 🔴 STORED UNCONDITIONALLY, AND THE GUARD THAT STOOD HERE COULD
          // NEVER PASS. `RefineRunAsync` seeds every row
          // `Status = Classifying` before it returns, so a
          // `status !== "classifying"` test was false on every submit: no quote
          // was ever stored, the state panel's `quoteFor` was always null, and
          // its Confirm button never rendered — decision 1's "get a click" with
          // no button to click.
          //
          // 🔴 The hazard that guard named — captioning a spend "free" —
          // cannot occur. The quote is a CEILING built at submit, before the
          // classifier has answered, so the server hardcodes `IsFree: false`
          // beside a real `RecordsToSpend` and a real `ModelCostUsd` (one
          // producer, one call site). And the cost sentence renders only on
          // `awaiting confirmation`, a status only a spending refinement
          // reaches; a refine-in-place that turns out free never shows it.
          quote.set({ refinementId: result.refinement.id, cost: result.cost });
        },
        onError: (e: unknown) => setError(refinementRefusalMessage(e)),
      },
    );
  };

  // Why Phoenix will not take one right now. Suppressed while something is in
  // flight, because the block ABOVE the list is the explanation then and the
  // blocker would only restate it.
  if (!state.canRefine) {
    if (inFlight != null) return null;
    return (
      <p
        className="m-0 text-caption text-gray-mid break-words"
        data-testid="ask-phoenix-blocked"
      >
        {refinementBlockerSentence(state.blocker)}
      </p>
    );
  }

  if (!canEdit || inFlight != null) return null;

  return (
    <section className="grid gap-space-1" data-testid="ask-phoenix-form">
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
        {copy.inputHeading}
      </h3>
      <p className="m-0 text-caption text-gray-mid break-words">{copy.intro}</p>
      <Label htmlFor={fieldId}>{copy.label}</Label>
      <Textarea
        id={fieldId}
        rows={2}
        value={text}
        placeholder={copy.placeholder}
        onChange={(e) => setText(e.target.value)}
        data-testid="ask-phoenix-input"
      />
      {error && (
        <p
          className="m-0 text-caption text-status-error"
          role="alert"
          data-testid="ask-phoenix-submit-error"
        >
          {error}
        </p>
      )}
      <div>
        <Button
          size="sm"
          variant="secondary"
          loading={refine.isPending}
          disabled={text.trim().length === 0}
          onClick={submit}
          data-testid="ask-phoenix-submit"
        >
          {refine.isPending ? copy.actionPending : copy.action}
        </Button>
      </div>
    </section>
  );
}

/**
 * The specialist's words, verbatim, with who asked and when.
 *
 * <p>🔴 Decision 5 — a refinement applied yesterday is still applied today, and
 * the person reading it may not be the person who typed it. The instruction is
 * never paraphrased and Phoenix's rationale never stands in for it: a record of
 * what Phoenix thought is not a record of what was asked.</p>
 */
function AskedLine({ refinement: r, tz }: { refinement: BuyerResearchRefinementDto; tz: string }) {
  const copy = researchCopy.askPhoenix;
  return (
    <div className="grid gap-[2px]" data-testid="ask-phoenix-asked">
      <p className="m-0 text-small text-onyx break-words">
        {copy.asked} <span className="font-semibold">“{r.text}”</span>{" "}
        <StatusBadge tone={refinementStatusTone(r.status)} data-testid="ask-phoenix-status">
          {copy.status[r.status] ?? r.status}
        </StatusBadge>
      </p>
      <span className="text-caption text-gray-mid break-words">
        <Timestamp value={r.createdAt} tz={tz} /> · {copy.askedBy(r.requestedByName)}
      </span>
    </div>
  );
}

/**
 * The statuses a refinement passes through on its way to being applied.
 *
 * <p>🔴 <b>`confirmed` is WORK IN PROGRESS, not an answer (R9).</b> Confirming
 * only queues the buy: the request writes the refinement `confirmed` and the
 * run `Refining` in one save, and the worker takes it from there to `applying`
 * and `applied`. Left out of this list, a confirmed refinement would be neither
 * waiting nor answered, and the panel would show the specialist nothing at all
 * between pressing Confirm and the batch landing. The run's `Refining` status is
 * what keeps the poll running meanwhile.</p>
 */
const IN_FLIGHT_STATUSES: readonly BuyerResearchRefinementStatus[] = [
  "classifying",
  "awaiting confirmation",
  "confirmed",
  "applying",
];

function isInFlight(r: BuyerResearchRefinementDto | null): r is BuyerResearchRefinementDto {
  return r != null && IN_FLIGHT_STATUSES.includes(r.status);
}

/**
 * The refinement this panel is waiting on, or null.
 *
 * <p>Read from <c>active</c> first and from the history second, because the
 * contract says <c>active</c> is "the refinement applied right now" and is
 * silent on where an unanswered one lives. Both readings resolve here, so a
 * server that puts a pending refinement on the pointer and one that leaves it
 * in the history produce the same surface.</p>
 *
 * <p>The history arrives newest first, so the first match is the newest — the
 * one a specialist is waiting on.</p>
 */
function pickInFlight(state: BuyerResearchRefinementStateDto): BuyerResearchRefinementDto | null {
  if (isInFlight(state.active)) return state.active;
  return state.history.find((r) => isInFlight(r)) ?? null;
}

/**
 * The newest refinement Phoenix answered by declining or failing, when that is
 * the last thing that happened.
 *
 * <p>Worth surfacing rather than burying in the history: a specialist who typed
 * something and got nothing back needs to read why without opening anything,
 * and the refusal is Phoenix's answer rather than an error.</p>
 */
function pickUnacted(
  state: BuyerResearchRefinementStateDto,
  inFlight: BuyerResearchRefinementDto | null,
): BuyerResearchRefinementDto | null {
  if (inFlight != null) return null;
  const newest = state.history[0];
  if (newest == null) return null;
  return newest.status === "refused" || newest.status === "failed" ? newest : null;
}

/**
 * What applying this refinement would cost, as sentences, in the order a
 * specialist reads them.
 *
 * <p>🔴 <b>`cost.isFree` is the SERVER's answer.</b> Free is not re-derived
 * from the two figures: a client that decided for itself would be free to call
 * a scoring call free the day the figures move.</p>
 *
 * <p>🔴 <b>THE TWO BUDGETS ARE NEVER ONE SENTENCE (RF6).</b> Records come off
 * Clef's shared daily allowance; model spend comes off this run's cap. A
 * sentence covering both would tell a specialist to wait for midnight over a
 * limit that has nothing to do with midnight.</p>
 *
 * <p>With no quote in hand this says what IS measured — the match count, which
 * is on the sticky record — and then says plainly that the spend figure is
 * missing. Deriving one from the run's page size would quote a number nobody
 * measured, and a specialist would approve it believing Phoenix had.</p>
 */
function costSentences(
  cost: BuyerResearchRefinementCostDto | null,
  refinement: BuyerResearchRefinementDto,
): string[] {
  const copy = researchCopy.askPhoenix;

  if (cost == null) {
    return refinement.matchCount != null
      ? [
          copy.costMatched(refinement.matchCount, refinement.matchCountApproximate),
          copy.costNotQuoted,
        ]
      : [copy.costNotQuoted];
  }

  if (cost.isFree) return [copy.costFree];

  const parts: string[] = [];

  if (refinement.matchCount != null) {
    parts.push(copy.costMatched(refinement.matchCount, refinement.matchCountApproximate));
  } else if (cost.recordsToSpend > 0) {
    // Records to spend and no count to spend them against. Said rather than
    // left as a gap where a number belongs.
    parts.push(copy.costNotCounted);
  }

  if (cost.recordsToSpend > 0) {
    parts.push(copy.costRecords(cost.recordsToSpend));
    if (cost.modelCostUsd > 0) parts.push(copy.costScoring(formatCost(cost.modelCostUsd)));
  } else if (cost.modelCostUsd > 0) {
    parts.push(copy.costScoringOnly(formatCost(cost.modelCostUsd)));
  }

  parts.push(copy.costBudgetLeft(formatCost(cost.remainingBudgetUsd)));
  return parts;
}
